import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import {
  useGetPlaceWorkLazyQuery, useUpdatePlaceWorkMutation,
} from '../generated/graphql';
import { Loading } from '../Loading';
import { ToastContext } from '../components/Toast';
import { GraphQLErrorDialog } from '../components/Dialogs';
import { Button } from '../components/Button';
import { FormActions } from '../components/FormActions';
import { Input } from '../components/Input';
import { SectionHeader } from '../components/SectionHeader';
import { useValidation } from '../utils/Validation';
import { ValidationRules, validatePlaceWorkStepName } from '../utils/ValidationRules';
import './PlaceCreateWorkPage.css';
import { TableScroll } from '../components/TableScroll';
import { Validated } from '../components/Validated';
import { ReorderHandleCell, ReorderHandleHeader, useRowReorder } from '../components/ReorderMode';

/** 追加工程の表の行のクラス。工程名に誤りのある行は、補修コードの手動入力項目の表と同じく背景と左端の線で強調する */
function getStepRowClassName(error: string | undefined): string | undefined {
  if (error !== undefined) {
    return 'PlaceCreateWorkPage-invalid_row';
  }
  return undefined;
}

/**
 * 追加工程の表の1行。id は登録済みの工程の id で、新しく足した工程は null にする。
 * key は行の見分け方で、並び替えても名前を変えても変わらない
 */
interface StepRow {
  key: string;
  id: number | null;
  name: string;
}

/**
 * 表に出している順に、行を保存の input に分ける。登録済みの工程は名前を変えていなくても順番が変わり得るので、すべて updated_steps に入れる。
 * display_order は登録済みと新しい工程を通した表の上からの位置（0始まり）にし、表の並びのとおりに保存されるようにする
 */
function buildStepInputs(steps: StepRow[]) {
  const createdSteps: { name: string, display_order: number }[] = [];
  const updatedSteps: { id: number, name: string, display_order: number }[] = [];
  steps.forEach((step, index) => {
    if (step.id === null) {
      createdSteps.push({ name: step.name, display_order: index });
    } else {
      updatedSteps.push({ id: step.id, name: step.name, display_order: index });
    }
  });
  return { createdSteps, updatedSteps };
}

/** 行の見分け方の並びに合わせて、行を並べ直す */
function orderStepsByKeys(steps: StepRow[], keys: string[]): StepRow[] {
  const result: StepRow[] = [];
  keys.forEach((key) => {
    const step = steps.find((candidate) => candidate.key === key);
    if (step !== undefined) {
      result.push(step);
    }
  });
  return result;
}

type IPlaceCreateWorkPageProps = RouteComponentProps<{ placeId: string, workId?: string }>

/** 新規の登録か既存の編集かで、見出し・送信ボタン・成功の通知の文言を出し分ける */
function getFormTexts(placeWorkId: number | null) {
  if (placeWorkId) {
    return {
      title: '工種の編集',
      submitLabel: '保存',
      completedMessage: '工種を保存しました',
    };
  }
  return {
    title: '工種の登録',
    submitLabel: '登録',
    completedMessage: '工種を登録しました',
  };
}

export const PlaceCreateWorkPage: React.FC<IPlaceCreateWorkPageProps> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const placeWorkId = props.match.params.workId === undefined ? null : parseInt(props.match.params.workId);
  const toastContext = React.useContext(ToastContext);
  const history = useHistory();
  const validation = useValidation(ValidationRules.PlaceWork);
  const formTexts = getFormTexts(placeWorkId);
  const listPath = `/places/${placeId}/works`;

  // 工種の名前
  const [name, setName] = validation.useState('name', '');
  // 基本工程の名前
  const [defaultStepName, setDefaultStepName] = validation.useState('defaultStepName', '初期工程', true);
  // 追加工程の表の行。登録済みの工程と新しく足した工程を、表に出している順に並べる
  const [steps, setSteps] = React.useState<StepRow[]>([]);
  // 保存したときに削除する工程IDのリスト。削除を決めた工程は表に出さない
  const [deletedStepIds, setDeletedStepIds] = React.useState<number[]>([]);
  // 新しく足した行の見分け方を作るための連番。描画のたびに数え直さないよう useRef に持つ
  const nextCreatedStepNumber = React.useRef(0);
  // 取っ手のドラッグと、取っ手を選んだ状態の上下の矢印キーで、行の順番を入れ替える
  const stepReorder = useRowReorder(
    steps.map((step) => step.key),
    (nextKeys) => setSteps(orderStepsByKeys(steps, nextKeys))
  );

  const addStep = function () {
    nextCreatedStepNumber.current += 1;
    setSteps([...steps, { key: `created-${nextCreatedStepNumber.current}`, id: null, name: '' }]);
  };

  const changeStepName = function (key: string, stepName: string) {
    setSteps(steps.map((step) => {
      if (step.key === key) {
        return { ...step, name: stepName };
      }
      return step;
    }));
  };

  // 行を表から外す。登録済みの工程は、保存したときに削除するよう削除する工程IDのリストに足す
  const deleteStep = function (target: StepRow) {
    setSteps(steps.filter((step) => step.key !== target.key));
    if (target.id !== null) {
      setDeletedStepIds([...deletedStepIds, target.id]);
    }
  };

  // データを編集する場合に既存のデータを取得するためのクエリ
  const [getPlaceWork, getPlaceWorkResult] = useGetPlaceWorkLazyQuery({
    onCompleted(data) {
      const work = data.getPlaceWork;
      setName(work.name);
      setDefaultStepName(work.default_step.name);
      // サーバーは追加工程を並び順で返すので、その順で表に出す
      setSteps(work.steps.map((step) => ({ key: `existing-${step.id}`, id: step.id, name: step.name })));
    },
  });

  // データの作成もしくは更新を行うクエリ
  const [updatePlaceWork, updatePlaceWorkResult] = useUpdatePlaceWorkMutation({
    onCompleted() {
      toastContext.showToast(formTexts.completedMessage, 'success');

      history.push(listPath);
    },
    onError() {
      toastContext.showToast('保存できませんでした。時間をおいて再実行してください。', 'error');
    },
  });

  // 既存のデータの修正を行う場合は現在のデータを取得する
  if (placeWorkId && !getPlaceWorkResult.called) {
    getPlaceWork({
      variables: { placeWorkId: placeWorkId },
    });
  }

  // 補修コードの手動入力項目の表と同じく、誤りは表の下に最初の1つだけを列の名前付きで出し、誤りのある行はすべて強調する。
  // 誤りのある行が1つでもあれば、工種名・基本工程の誤りと同じく保存できないようにする
  const stepErrors = steps.map(step => validatePlaceWorkStepName(step.name));
  const firstStepError = stepErrors.find(error => error !== undefined);
  const hasInvalidStep = firstStepError !== undefined;
  let stepsValidationMessage;
  if (firstStepError !== undefined) {
    stepsValidationMessage = `工程名: ${firstStepError}`;
  }

  // エラー処理
  if (getPlaceWorkResult.error) {
    return <GraphQLErrorDialog error={getPlaceWorkResult.error} />;
  }

  return (
    <div className="PlaceCreateWorkPage-container">
      <Loading open={updatePlaceWorkResult.loading || getPlaceWorkResult.loading} />
      <SectionHeader title={formTexts.title} />
      <div className="App-form">
        <Input
          label="工種名"
          required={true}
          type="string"
          onChange={(event) => setName(event.target.value)}
          value={name}
          validationMessage={validation.get('name')}
        />
        <Input
          label="基本工程"
          required={true}
          type="string"
          onChange={(event) => setDefaultStepName(event.target.value)}
          value={defaultStepName}
          validationMessage={validation.get('defaultStepName')}
        />
        <div>
          <label className="App-label">追加工程</label>
          <Validated message={stepsValidationMessage}>
            <TableScroll storageKey="placeWorkSteps">
              <table className="App-table">
                <thead>
                  <tr>
                    <ReorderHandleHeader />
                    <th>
                      工程名
                      <span className="App-label_required" aria-hidden="true">
                        *
                      </span>
                    </th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {steps.map((step, index) => (
                    // 誤りのある行の強調は、行の左端（取っ手の列）から背景と赤い線で示す
                    <tr key={step.key} className={getStepRowClassName(stepErrors[index])} {...stepReorder.getRowProps(step.key)}>
                      <ReorderHandleCell
                        {...stepReorder.getHandleCellProps(step.key, index)}
                        disabled={updatePlaceWorkResult.loading}
                      />
                      <td>
                        <Input
                          type="string"
                          onChange={(event) => changeStepName(step.key, event.target.value)}
                          value={step.name}
                        />
                      </td>
                      <td className="App-table_button_cell">
                        <Button
                          type="button"
                          mini={true}
                          color="red"
                          onClick={() => deleteStep(step)}
                        >
                          削除
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </Validated>
          {/* 補修コードの入力画面の表と同じく、表の下に間を空けて「行を追加」を置く */}
          <div className="PlaceCreateWorkPage-table_add_row">
            <Button
              type="button"
              mini={true}
              color="white"
              onClick={() => addStep()}
            >
              行を追加
            </Button>
          </div>
        </div>
        <FormActions
          submitLabel={formTexts.submitLabel}
          submitDisabled={validation.isInvalid || hasInvalidStep || updatePlaceWorkResult.loading}
          onCancel={() => history.push(listPath)}
          onSubmit={() => {
            const stepInputs = buildStepInputs(steps);
            updatePlaceWork({
              variables: {
                input: {
                  id: placeWorkId,
                  place_id: placeId,
                  name: name,
                  default_step: {
                    name: defaultStepName
                  },
                  created_steps: stepInputs.createdSteps,
                  // 削除する工程は、削除したときに deleteStep が表の行から外している
                  updated_steps: stepInputs.updatedSteps,
                  deleted_step_ids: deletedStepIds,
                },
              },
            });
          }}
        />
      </div>
    </div>
  );
};
